import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../../hooks/use-toast';

import { Plus, Loader2, Trash2 } from 'lucide-react';
import { dealsAPI, plansAPI } from '../../services/api';
import { usePrivacy } from '../../contexts/PrivacyContext';

const GST_RATE = 0.18;

const EditDealDialog = ({ open, onOpenChange, deal, onSuccess }) => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    plan_id: '',
    total_users: 0,
    extra_user_rate: 0,
    addon_items: [],
    one_time_setup_charge: 0,
    notes: '',
    // revision reason
    revision_reason: ''
  });

  const [addonItem, setAddonItem] = useState({ name: '', price: 0 });

  // Fetch plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await plansAPI.list();
        setPlans(res.data || []);
      } catch (e) { console.error("Failed to fetch plans", e); }
    };
    if (open) fetchPlans();
  }, [open]);

  // Initialize form from deal data
  useEffect(() => {
    if (deal && open) {
      // Parse line items to reconstruct state
      let planId = deal.plan_id;
      let setupCharge = 0;
      let addons = [];
      let planSubscriptionPrice = 0;
      
      // Try to find setup charge and plan subscription in line items
      (deal.line_items || []).forEach(item => {
        const desc = (item.description || '').toLowerCase();
        const price = item.unit_price || item.total_price || 0;
        
        if (desc.includes('setup') || desc.includes('one-time') || desc.includes('onetime')) {
            setupCharge += price;
        } else if (desc.includes('plan') || desc.includes('subscription')) {
            // This is the plan subscription - store the price for reference
            planSubscriptionPrice = price;
        } else {
            // Everything else is an addon (AMPL, pep, etc.)
            addons.push({
                id: item.id || Date.now() + Math.random(),
                name: item.description,
                price: price
            });
        }
      });

      // Try to calculate extra user rate from the stored plan subscription price
      // planSubscriptionPrice = basePlanPrice + (extraUsers * extraUserRate)
      // So we need to find the plan first
      let extraRate = 0;
      const plan = plans.find(p => p.id === planId);
      if (plan && plan.base_price_per_month && deal.total_users) {
        const baseCapacity = plan.base_user_capacity || 0;
        const extraUsers = Math.max(0, deal.total_users - baseCapacity);
        if (extraUsers > 0 && planSubscriptionPrice > plan.base_price_per_month) {
          // Reverse calculate extra user rate
          extraRate = Math.round((planSubscriptionPrice - plan.base_price_per_month) / extraUsers);
        }
      }

      setFormData({
        plan_id: planId || '',
        total_users: deal.total_users || 0,
        extra_user_rate: extraRate,
        addon_items: addons,
        one_time_setup_charge: setupCharge,
        notes: deal.notes || '',
        revision_reason: ''
      });
    }
  }, [deal, open, plans]);

  const selectedPlan = plans.find(p => p.id === formData.plan_id);

  // Calculations
  const baseCapacity = selectedPlan?.base_user_capacity || 0;
  const extraUsers = Math.max(0, formData.total_users - baseCapacity);
  
  const calculateTotals = () => {
    const basePlanPrice = selectedPlan?.base_price_per_month || 0;
    const extraUsersCost = extraUsers * formData.extra_user_rate;
    const planAmount = basePlanPrice + extraUsersCost;
    const addonsTotal = formData.addon_items.reduce((sum, item) => sum + item.price, 0);
    const subtotal = planAmount + addonsTotal + formData.one_time_setup_charge;
    const gstAmount = subtotal * GST_RATE;
    const grandTotal = subtotal + gstAmount;
    return { basePlanPrice, extraUsersCost, planAmount, addonsTotal, subtotal, gstAmount, grandTotal };
  };

  const totals = calculateTotals();

  const handleAddAddon = () => {
    if (addonItem.name && addonItem.price > 0) {
      setFormData(prev => ({
        ...prev,
        addon_items: [...prev.addon_items, { ...addonItem, id: Date.now() }]
      }));
      setAddonItem({ name: '', price: 0 });
    }
  };

  const removeAddon = (id) => {
    setFormData(prev => ({
      ...prev,
      addon_items: prev.addon_items.filter(item => item.id !== id)
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Construct line items
      const line_items = [
          { description: 'Plan Subscription', quantity: 1, unit_price: totals.planAmount },
          ...formData.addon_items.map(item => ({ description: item.name, quantity: 1, unit_price: item.price })),
          ...(formData.one_time_setup_charge > 0 ? [{ description: 'One-time Setup', quantity: 1, unit_price: formData.one_time_setup_charge }] : [])
      ];

      await dealsAPI.update(deal.id, {
        plan_id: formData.plan_id,
        total_users: formData.total_users,
        notes: formData.revision_reason ? `${formData.notes}\n[Revision: ${formData.revision_reason}]` : formData.notes,
        line_items: line_items,
        deal_value_estimate: totals.grandTotal
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update deal", error);
      toast({
        variant: 'destructive',
        title: 'Failed to update deal',
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Deal</DialogTitle>
          <DialogDescription>
            Modify deal terms. Changes will be tracked in revision history.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Plan & Users */}
          <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Plan</Label>
                <Select 
                  value={formData.plan_id} 
                  onValueChange={(v) => setFormData({...formData, plan_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.plan_name} - {formatCurrency(plan.base_price_per_month)}/mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Total Users</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.total_users}
                  onChange={(e) => setFormData({...formData, total_users: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            {extraUsers > 0 && (
              <div className="mt-4 p-3 bg-amber-50 rounded border border-amber-200">
                <Label className="text-amber-700">Per Extra User Rate (per month)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.extra_user_rate}
                  onChange={(e) => setFormData({...formData, extra_user_rate: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
                <p className="text-xs text-amber-600 mt-1">
                   +{extraUsers} extra users
                </p>
              </div>
            )}
          </div>

          {/* Addons */}
          <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border">
            <h3 className="text-sm font-semibold mb-3">Addon Items</h3>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Input
                placeholder="Name"
                value={addonItem.name}
                onChange={(e) => setAddonItem({...addonItem, name: e.target.value})}
              />
              <Input
                type="number"
                placeholder="Price"
                value={addonItem.price || ''}
                onChange={(e) => setAddonItem({...addonItem, price: parseInt(e.target.value) || 0})}
              />
              <Button variant="outline" onClick={handleAddAddon}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-1">
              {formData.addon_items.map(item => (
                <div key={item.id} className="flex justify-between p-2 bg-white rounded border">
                  <span>{item.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatCurrency(item.price)}</span>
                    <Button variant="ghost" size="sm" className="h-6 w-6 text-red-500" onClick={() => removeAddon(item.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Setup Charge */}
          <div className="grid gap-2">
            <Label>One-Time Setup Charge</Label>
            <Input
              type="number"
              value={formData.one_time_setup_charge}
              onChange={(e) => setFormData({...formData, one_time_setup_charge: parseInt(e.target.value) || 0})}
            />
          </div>

          {/* Revision Reason */}
          <div className="grid gap-2">
            <Label>Revision Reason / Notes *</Label>
            <Textarea
              value={formData.revision_reason}
              onChange={(e) => setFormData({...formData, revision_reason: e.target.value})}
              placeholder="Why is this deal being modified?"
            />
          </div>

          {/* Totals */}
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
             <div className="flex justify-between font-bold text-lg text-emerald-800">
               <span>Grand Total</span>
               <span>{formatCurrency(totals.grandTotal)}</span>
             </div>
             <p className="text-xs text-emerald-600 text-right mt-1">Includes 18% GST</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={loading || !formData.plan_id || !formData.revision_reason}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Update Deal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditDealDialog;
